import { FormEvent, useEffect, useRef, useState } from 'react';

import { submitAccessRequest } from '../api/access-request-api';

type RequestAccessModalProps = {
  open: boolean;
  onClose: () => void;
};

type FormState = {
  fullName: string;
  email: string;
  company: string;
  roleTitle: string;
  machineGoal: string;
  website: string;
};

const emptyForm: FormState = {
  fullName: '',
  email: '',
  company: '',
  roleTitle: '',
  machineGoal: '',
  website: '',
};

export function RequestAccessModal({ open, onClose }: RequestAccessModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const submittingRef = useRef(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [confirmationFailed, setConfirmationFailed] = useState(false);

  useEffect(() => { submittingRef.current = submitting; }, [submitting]);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add('r42-modal-open');
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !submittingRef.current) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    window.setTimeout(() => dialogRef.current?.querySelector<HTMLInputElement>('input[name="fullName"]')?.focus(), 0);
    return () => {
      document.body.classList.remove('r42-modal-open');
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!submitted) return;
    const timer = window.setTimeout(() => {
      setSubmitted(false);
      setForm(emptyForm);
      setConfirmationFailed(false);
      onClose();
    }, 4200);
    return () => window.clearTimeout(timer);
  }, [submitted, onClose]);

  if (!open) return null;

  function close() {
    if (submitting) return;
    setError('');
    setSubmitted(false);
    setConfirmationFailed(false);
    onClose();
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      setError('Enter a valid work email.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitAccessRequest({
        full_name: form.fullName.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        role_title: form.roleTitle.trim() || undefined,
        machine_goal: form.machineGoal.trim() || undefined,
        website: form.website,
        source_path: window.location.pathname,
      });
      setConfirmationFailed(result.confirmation_email_status === 'FAILED');
      setSubmitted(true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Route42 could not submit your request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="r42-access-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) close();
    }}>
      <div
        ref={dialogRef}
        className="r42-access-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="r42-access-title"
      >
        <button className="r42-access-modal-close" type="button" aria-label="Close request access form" onClick={close}>
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button>

        {submitted ? (
          <div className="r42-access-success" role="status">
            <span className="r42-access-success-icon"><i className="bi bi-check-lg" aria-hidden="true" /></span>
            <span className="r42-marketing-eyebrow">Request received</span>
            <h2 id="r42-access-title">You’re on the list.</h2>
            <p>{confirmationFailed ? 'Your request is safely recorded. We’ll review it and follow up with next steps. We could not send the confirmation email, but you do not need to resubmit.' : 'Your request is in. We’ll review it and follow up with next steps. A confirmation email is on its way.'}</p>
          </div>
        ) : (
          <>
            <span className="r42-marketing-eyebrow">Early access</span>
            <h2 id="r42-access-title">Bring Machine Intelligence into the conversation.</h2>
            <p className="r42-access-modal-intro">Tell us just enough to understand who you are and what you want to make more knowable.</p>

            <form className="r42-access-form" onSubmit={submit} noValidate>
              <div className="r42-access-field-row">
                <label>
                  <span>Full name</span>
                  <input name="fullName" autoComplete="name" required maxLength={120} value={form.fullName} onChange={(event) => setForm({ ...form, fullName: event.target.value })} />
                </label>
                <label>
                  <span>Work email</span>
                  <input type="email" name="email" autoComplete="email" required maxLength={254} value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
                </label>
              </div>
              <div className="r42-access-field-row">
                <label>
                  <span>Company</span>
                  <input name="company" autoComplete="organization" required maxLength={160} value={form.company} onChange={(event) => setForm({ ...form, company: event.target.value })} />
                </label>
                <label>
                  <span>Role / title <small>Optional</small></span>
                  <input name="roleTitle" autoComplete="organization-title" maxLength={160} value={form.roleTitle} onChange={(event) => setForm({ ...form, roleTitle: event.target.value })} />
                </label>
              </div>
              <label>
                <span>What would you like Route42 to help you understand? <small>Optional</small></span>
                <textarea name="machineGoal" rows={3} maxLength={700} placeholder="A machine, reliability problem, knowledge gap, or workflow..." value={form.machineGoal} onChange={(event) => setForm({ ...form, machineGoal: event.target.value })} />
              </label>
              <label className="r42-access-honeypot" aria-hidden="true">
                <span>Website</span>
                <input name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(event) => setForm({ ...form, website: event.target.value })} />
              </label>

              {error ? <div className="r42-access-form-error" role="alert">{error}</div> : null}

              <button className="r42-marketing-primary r42-access-submit" type="submit" disabled={submitting || !form.fullName.trim() || !form.email.trim() || !form.company.trim()}>
                {submitting ? <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Sending request</> : <>Request access <i className="bi bi-arrow-right" aria-hidden="true" /></>}
              </button>
              <p className="r42-access-privacy">We’ll use this information only to evaluate and respond to your Route42 access request.</p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
